const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'h2', 'p', 'a', 'strong', 'em', 'br', 'ul', 'ol', 'li', 'img', 'iframe', 'blockquote',
]);
const SAFE_SCHEME = /^(https?:|mailto:)/i;
const SAFE_IMG_SCHEME = /^https?:/i;
// Strict YouTube embed URL allowlist. Anchored. Must be the embed path. Optional
// query string with a small allowed character set (no quotes, no <, no \).
const YOUTUBE_SRC = /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com)\/embed\/[A-Za-z0-9_-]{6,20}(\?[\w=&-]{0,200})?$/;
const VOID_TAGS: ReadonlySet<string> = new Set(['br', 'img']);

function parseAttribs(raw: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /([a-zA-Z][a-zA-Z0-9-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const key = m[1];
    if (!key) continue;
    attrs[key.toLowerCase()] = m[2] ?? m[3] ?? m[4] ?? '';
  }
  return attrs;
}

function escapeAttr(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function processTag(inner: string): string | null {
  const isClose = inner.startsWith('/');
  // `<br/>` and `<img ... />` show up with a trailing slash in self-closing form;
  // strip it before parsing the attribute string.
  const stripped = (isClose ? inner.slice(1) : inner).trim().replace(/\/$/, '');
  const spaceIdx = stripped.search(/\s/);
  const tag = (spaceIdx === -1 ? stripped : stripped.slice(0, spaceIdx)).toLowerCase();

  if (!ALLOWED_TAGS.has(tag)) return null;
  if (isClose) {
    if (VOID_TAGS.has(tag)) return null;
    return `/${tag}`;
  }
  if (tag === 'br') return 'br';

  if (tag === 'a') {
    const attrs = parseAttribs(spaceIdx === -1 ? '' : stripped.slice(spaceIdx));
    let href = attrs['href'] ?? '';
    if (href && !SAFE_SCHEME.test(href)) href = '';
    const parts = ['a'];
    if (href) parts.push(`href="${escapeAttr(href)}"`);
    parts.push('rel="noopener noreferrer"', 'target="_blank"');
    return parts.join(' ');
  }

  if (tag === 'img') {
    const attrs = parseAttribs(spaceIdx === -1 ? '' : stripped.slice(spaceIdx));
    const src = attrs['src'] ?? '';
    if (!src || !SAFE_IMG_SCHEME.test(src)) return null;
    const parts = ['img', `src="${escapeAttr(src)}"`];
    const alt = attrs['alt'] ?? '';
    parts.push(`alt="${escapeAttr(alt)}"`);
    parts.push('loading="lazy"');
    return parts.join(' ');
  }

  if (tag === 'iframe') {
    const attrs = parseAttribs(spaceIdx === -1 ? '' : stripped.slice(spaceIdx));
    const src = attrs['src'] ?? '';
    if (!src || !YOUTUBE_SRC.test(src)) return null;
    const parts = [
      'iframe',
      `src="${escapeAttr(src)}"`,
      'width="560"',
      'height="315"',
      'allowfullscreen',
      'loading="lazy"',
      'referrerpolicy="strict-origin-when-cross-origin"',
      'sandbox="allow-scripts allow-same-origin allow-presentation"',
      'title="YouTube video player"',
      'frameborder="0"',
    ];
    return parts.join(' ');
  }

  return tag;
}

export function sanitizeRichText(html: string): string {
  let out = '';
  let i = 0;
  while (i < html.length) {
    if (html[i] === '<') {
      const end = html.indexOf('>', i);
      if (end === -1) {
        out += '&lt;';
        i++;
        continue;
      }
      const inner = html.slice(i + 1, end);
      if (!inner.startsWith('!') && !inner.startsWith('?')) {
        const p = processTag(inner);
        if (p !== null) out += `<${p}>`;
      }
      i = end + 1;
    } else if (html[i] === '&') {
      const end = html.indexOf(';', i);
      if (end !== -1 && end - i <= 10) {
        out += html.slice(i, end + 1);
        i = end + 1;
      } else {
        out += '&amp;';
        i++;
      }
    } else {
      out += html[i++];
    }
  }
  return out;
}

export function stripHtml(html: string): string {
  return sanitizeRichText(html).replace(/<[^>]+>/g, '');
}
